import React, { useState, useEffect, useRef, useCallback } from "react";
import { Container, Button, Spinner, Alert, Badge } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaTruck,
  FaExpand,
  FaCompress,
  FaCrosshairs,
  FaMap,
  FaLayerGroup,
  FaSatellite,
  FaMountain,
  FaStreetView,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";
import { userAPI } from "../../api/apiSystem";
import { useTranslation } from "react-i18next";
import { translateBackendMessage } from "../../api/apiSystem";
import Pusher from "pusher-js";
import ClientChatBox from "../chat/ClientChatBox";
import "../clientTable/DarkClientStyles.css";

// ── Lazy loader لـ Leaflet ───────────────────────────────────────────────────
let lfLoaded = false;
let lfLoading = false;
let lfCallbacks = [];
let lfError = null;

const loadLeaflet = (onSuccess, onError) => {
  if (lfLoaded) { onSuccess(); return; }
  if (lfError)  { onError(lfError); return; }
  lfCallbacks.push({ onSuccess, onError });
  if (lfLoading) return;
  lfLoading = true;

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
  document.head.appendChild(link);

  const script = document.createElement("script");
  script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
  script.async = true;
  script.onload = () => {
    lfLoaded = true;
    lfLoading = false;
    lfCallbacks.forEach(({ onSuccess: cb }) => cb());
    lfCallbacks = [];
  };
  script.onerror = () => {
    lfError = "Failed to load Leaflet";
    lfLoading = false;
    lfCallbacks.forEach(({ onError: cb }) => cb(lfError));
    lfCallbacks = [];
  };
  document.head.appendChild(script);
};

const useLeaflet = () => {
  const [loaded, setLoaded] = useState(lfLoaded);
  const [error, setError] = useState(lfError);

  useEffect(() => {
    if (lfLoaded) { setLoaded(true); return; }
    const t = setTimeout(() => { if (!lfLoaded) setError("Map load timeout"); }, 10000);
    loadLeaflet(
      () => { clearTimeout(t); setLoaded(true); },
      (e) => { clearTimeout(t); setError(e); }
    );
    return () => clearTimeout(t);
  }, []);

  return { loaded, error };
};

// ── أنماط الخريطة المتاحة ──────────────────────────────────────────────────
const MAP_STYLES = {
  streets: {
    name: "Streets",
    icon: FaStreetView,
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; OpenStreetMap',
    maxZoom: 19,
  },
  satellite: {
    name: "Satellite",
    icon: FaSatellite,
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: '&copy; Esri',
    maxZoom: 19,
  },
  terrain: {
    name: "Terrain",
    icon: FaMountain,
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: '&copy; OpenTopoMap',
    maxZoom: 17,
  },
};

export default function BookingEmployeeLiveTrack() {
  const { t } = useTranslation();
  const { id, employeeId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [initialPosition, setInitialPosition] = useState(null);
  const [driverData, setDriverData] = useState(null);

  // ── حالات التحكم في الخريطة ────────────────────────────────────────────
  const [currentStyle, setCurrentStyle] = useState("streets");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showStyleMenu, setShowStyleMenu] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [autoFollow, setAutoFollow] = useState(true);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);
  const tileLayerRef = useRef(null);
  const animationRef = useRef(null);
  const pusherRef = useRef(null);
  const lastPositionRef = useRef(null);
  const { loaded: leafletLoaded, error: leafletError } = useLeaflet();

  const PUSHER_KEY = "1a6dafa4504c853b7f61";
  const PUSHER_CLUSTER = "mt1";

  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null
  );

  // ── Fetch Data ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (id) fetchInitialData();
    else {
      setError("Order ID is missing");
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (initialPosition && leafletLoaded && !mapInstanceRef.current) initMap();
  }, [initialPosition, leafletLoaded]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await userAPI.employees.bookingLiveTrack(employeeId, id);
      if (response.data && response.data.data) {
        const data = response.data.data;
        const position = [parseFloat(data.lat), parseFloat(data.lng)];
        setInitialPosition(position);
        lastPositionRef.current = position;
        setDriverData(data);
        setupPusher();
      } else {
        setError("No tracking data available");
      }
    } catch (err) {
      console.error("Error fetching live track data:", err);
      setError(
        translateBackendMessage(
          err?.response?.data || err,
          t("general.failed_to_fetch_tracking")
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Init Map ────────────────────────────────────────────────────────────
  const initMap = () => {
    if (!initialPosition || !window.L || mapInstanceRef.current) return;
    const L = window.L;

    const map = L.map(mapRef.current, {
      center: initialPosition,
      zoom: 16,
      zoomControl: false, // سنضيفها يدوياً
      attributionControl: true,
      fullscreenControl: false,
    });

    // ── Tile Layer ──────────────────────────────────────────────────────
    const style = MAP_STYLES[currentStyle];
    const tileLayer = L.tileLayer(style.url, {
      maxZoom: style.maxZoom,
      attribution: style.attribution,
    }).addTo(map);
    tileLayerRef.current = tileLayer;

    // ── أيقونة السائق ────────────────────────────────────────────────────
    const driverIcon = L.divIcon({
      className: "",
      html: `
        <div style="
          width:50px;height:50px;
          background:#ff6b00;
          border:4px solid #fff;
          border-radius:50%;
          box-shadow:0 4px 14px rgba(255,107,0,.6);
          display:flex;align-items:center;justify-content:center;
          font-size:24px;
          animation: pulse 2s infinite;
        ">
          🚚
        </div>
        <style>
          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 rgba(255,107,0,.7); }
            70% { box-shadow: 0 0 0 20px rgba(255,107,0,0); }
            100% { box-shadow: 0 0 0 0 rgba(255,107,0,0); }
          }
        </style>
      `,
      iconSize: [50, 50],
      iconAnchor: [25, 25],
    });

    const marker = L.marker(initialPosition, {
      icon: driverIcon,
      title: "Driver Location",
    }).addTo(map);

    // ── إضافة التحكم في التكبير (أسفل يمين) ──────────────────────────────
    L.control.zoom({ position: "bottomright" }).addTo(map);

    // ── إضافة مقياس المسافة (أسفل يسار) ──────────────────────────────────
    L.control.scale({
      metric: true,
      imperial: false,
      position: "bottomleft",
    }).addTo(map);

    mapInstanceRef.current = map;
    markerInstanceRef.current = marker;

    console.log("✅ Map initialized with controls");
  };

  // ── Map Control Handlers ────────────────────────────────────────────────
  const handleZoomIn = () => {
    mapInstanceRef.current?.zoomIn();
  };

  const handleZoomOut = () => {
    mapInstanceRef.current?.zoomOut();
  };

  const handleRecenter = () => {
    const map = mapInstanceRef.current;
    const pos = lastPositionRef.current;
    if (map && pos) {
      map.setView(pos, 16, { animate: true });
      setAutoFollow(true);
    }
  };

  const handleToggleFullscreen = () => {
    const elem = mapRef.current?.parentElement || document.documentElement;
    if (!document.fullscreenElement) {
      elem.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const handleChangeStyle = (styleKey) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const style = MAP_STYLES[styleKey];
    if (!style) return;

    // إزالة الطبقة القديمة
    if (tileLayerRef.current) {
      map.removeLayer(tileLayerRef.current);
    }
    // إضافة الطبقة الجديدة
    const newLayer = window.L.tileLayer(style.url, {
      maxZoom: style.maxZoom,
      attribution: style.attribution,
    }).addTo(map);
    tileLayerRef.current = newLayer;
    setCurrentStyle(styleKey);
    setShowStyleMenu(false);
  };

  const handleLocateMe = () => {
    const map = mapInstanceRef.current;
    if (!map) return;
    map.locate({ setView: true, maxZoom: 16 });
  };

  const toggleAutoFollow = () => {
    setAutoFollow((prev) => !prev);
  };

  // ── Pusher ──────────────────────────────────────────────────────────────
  const setupPusher = () => {
    if (!id) return;
    try {
      if (pusherRef.current) pusherRef.current.disconnect();

      const pusherInstance = new Pusher(PUSHER_KEY, {
        cluster: PUSHER_CLUSTER,
        forceTLS: true,
      });

      const channel = pusherInstance.subscribe(`deliveries.${id}`);

      channel.bind("booking-location-updated", (data) => {
        if (data && data.lat && data.lng) {
          const newPosition = [parseFloat(data.lat), parseFloat(data.lng)];
          lastPositionRef.current = newPosition;
          smoothMoveTo(newPosition);
        }
      });

      pusherRef.current = pusherInstance;
    } catch (err) {
      console.error("Error setting up Pusher:", err);
    }
  };

  // ── Smooth Move ─────────────────────────────────────────────────────────
  const smoothMoveTo = (newPosition) => {
    const marker = markerInstanceRef.current;
    const map = mapInstanceRef.current;
    if (!marker || !map) return;

    const oldPos = marker.getLatLng();
    const oldLat = oldPos.lat;
    const oldLng = oldPos.lng;

    const deltaLat = newPosition[0] - oldLat;
    const deltaLng = newPosition[1] - oldLng;

    const steps = 50;
    let currentStep = 0;

    if (animationRef.current) clearInterval(animationRef.current);

    animationRef.current = setInterval(() => {
      currentStep++;
      const lat = oldLat + (deltaLat * currentStep) / steps;
      const lng = oldLng + (deltaLng * currentStep) / steps;
      const position = [lat, lng];

      marker.setLatLng(position);

      // ✅ التمركز التلقائي إذا كان مفعّلاً
      if (autoFollow) {
        map.panTo(position, { animate: false });
      }

      if (currentStep >= steps) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    }, 20);
  };

  // ── Cleanup ─────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (pusherRef.current) pusherRef.current.disconnect();
      if (animationRef.current) clearInterval(animationRef.current);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const handleBack = () => navigate(-1);

  // ── Loading / Error States ──────────────────────────────────────────────
  if (loading)
    return (
      <Container className="dark-client-page d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <Spinner animation="border" variant="warning" />
          <p>{t("loading")}</p>
        </div>
      </Container>
    );

  if (error || leafletError)
    return (
      <Container className="dark-client-page p-4">
        <Alert variant="danger">
          <h5>{t("error")}</h5>
          {error || leafletError}
          <div className="mt-3">
            <Button variant="outline-danger" onClick={handleBack}>
              <FaArrowLeft className="me-2" />
              {t("back")}
            </Button>
          </div>
        </Alert>
      </Container>
    );

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <Container fluid className="p-0 position-relative">
        <div
          id="map"
          ref={mapRef}
          style={{
            height: "100vh",
            width: "100%",
            borderRadius: 0,
            zIndex: 0,
          }}
        />

        {/* ═════════════════════════════════════════════════════════════════
            زر الرجوع (أعلى يسار)
           ═════════════════════════════════════════════════════════════════ */}
        <div
          className="position-absolute top-0 start-0 p-3"
          style={{ zIndex: 1000 }}
        >
          <Button variant="warning" onClick={handleBack} className="shadow">
            <FaArrowLeft className="me-2" />
            {t("back")}
          </Button>
        </div>

        {/* ═════════════════════════════════════════════════════════════════
            أدوات التحكم في الخريطة (أعلى يمين)
           ═════════════════════════════════════════════════════════════════ */}
        <div
          className="position-absolute top-0 end-0 p-3"
          style={{ zIndex: 1000 }}
        >
          <div
            className="map-controls-panel"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              background: "rgba(255,255,255,0.95)",
              borderRadius: "12px",
              padding: "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              backdropFilter: "blur(10px)",
            }}
          >
            {/* زر التكبير */}
            <button
              className="map-ctrl-btn"
              onClick={handleZoomIn}
              title="Zoom In"
            >
              <span style={{ fontSize: "20px", fontWeight: "bold" }}>+</span>
            </button>

            {/* زر التصغير */}
            <button
              className="map-ctrl-btn"
              onClick={handleZoomOut}
              title="Zoom Out"
            >
              <span style={{ fontSize: "20px", fontWeight: "bold" }}>−</span>
            </button>

            <div className="map-ctrl-divider" />

            {/* زر إعادة التمركز على السائق */}
            <button
              className={`map-ctrl-btn ${autoFollow ? "active" : ""}`}
              onClick={handleRecenter}
              title="Recenter on Driver"
            >
              <FaCrosshairs />
            </button>

            {/* زر ملء الشاشة */}
            <button
              className="map-ctrl-btn"
              onClick={handleToggleFullscreen}
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>

            {/* زر موقعي */}
            <button
              className="map-ctrl-btn"
              onClick={handleLocateMe}
              title="My Location"
            >
              📍
            </button>

            <div className="map-ctrl-divider" />

            {/* زر تغيير نوع الخريطة */}
            <button
              className={`map-ctrl-btn ${showStyleMenu ? "active" : ""}`}
              onClick={() => setShowStyleMenu((prev) => !prev)}
              title="Map Style"
            >
              <FaLayerGroup />
            </button>

            {/* زر معلومات السائق */}
            <button
              className={`map-ctrl-btn ${showInfoPanel ? "active" : ""}`}
              onClick={() => setShowInfoPanel((prev) => !prev)}
              title="Driver Info"
            >
              <FaInfoCircle />
            </button>

            {/* زر متابعة تلقائية */}
            <button
              className={`map-ctrl-btn ${autoFollow ? "active" : ""}`}
              onClick={toggleAutoFollow}
              title={autoFollow ? "Auto-follow: ON" : "Auto-follow: OFF"}
            >
              {autoFollow ? "🎯" : "🚫"}
            </button>
          </div>

          {/* قائمة أنماط الخريطة */}
          {showStyleMenu && (
            <div
              className="map-style-menu"
              style={{
                marginTop: "8px",
                background: "rgba(255,255,255,0.98)",
                borderRadius: "12px",
                padding: "8px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                minWidth: "160px",
                backdropFilter: "blur(10px)",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "#64748b",
                  padding: "6px 10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Map Style
              </div>
              {Object.entries(MAP_STYLES).map(([key, style]) => {
                const Icon = style.icon;
                return (
                  <button
                    key={key}
                    className={`map-style-item ${
                      currentStyle === key ? "active" : ""
                    }`}
                    onClick={() => handleChangeStyle(key)}
                  >
                    <Icon />
                    <span>{style.name}</span>
                    {currentStyle === key && <span className="check">✓</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ═════════════════════════════════════════════════════════════════
            لوحة معلومات السائق (أسفل يمين)
           ═════════════════════════════════════════════════════════════════ */}
        {showInfoPanel && driverData && (
          <div
            className="position-absolute bottom-0 end-0 p-3"
            style={{ zIndex: 1000, maxWidth: "320px", width: "100%" }}
          >
            <div
              className="driver-info-panel"
              style={{
                background: "rgba(255,255,255,0.98)",
                borderRadius: "14px",
                padding: "16px",
                boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255,123,0,0.15)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #ff7b00, #e66d00)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                    }}
                  >
                    🚚
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        color: "#061730",
                      }}
                    >
                      Driver
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#22c55e",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span
                        style={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: "#22c55e",
                          animation: "blink 1.5s infinite",
                        }}
                      />
                      Live
                    </div>
                  </div>
                </div>
                <button
                  className="map-ctrl-btn-close"
                  onClick={() => setShowInfoPanel(false)}
                  title="Close"
                >
                  <FaTimes />
                </button>
              </div>

              <div
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  borderTop: "1px solid #e2e8f0",
                  paddingTop: "10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "4px 0",
                  }}
                >
                  <span>{t('clientTable.tableHeaders.orderId')}:</span>
                  <strong style={{ color: "#061730" }}>{id}</strong>
                </div>
                {driverData.lat && driverData.lng && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "4px 0",
                    }}
                  >
                    <span>Position:</span>
                    <strong style={{ color: "#061730", fontSize: "10px" }}>
                      {parseFloat(driverData.lat).toFixed(4)},{" "}
                      {parseFloat(driverData.lng).toFixed(4)}
                    </strong>
                  </div>
                )}
                {driverData.updated_at && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "4px 0",
                    }}
                  >
                    <span>Updated:</span>
                    <strong style={{ color: "#061730", fontSize: "10px" }}>
                      {new Date(driverData.updated_at).toLocaleTimeString()}
                    </strong>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═════════════════════════════════════════════════════════════════
            شارة "التتبع المباشر" (أسفل يسار)
           ═════════════════════════════════════════════════════════════════ */}
        <div
          className="position-absolute bottom-0 start-0 p-3"
          style={{ zIndex: 1000 }}
        >
          <Badge bg="info" className="p-2 fs-6">
            <FaTruck className="me-2" />
            {t("general.live_tracking_active")}
          </Badge>
        </div>
      </Container>

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}